import { MainThunkDispatch } from "../../../Reducers"
import { TemplateAPIType } from "../../Types"
import { apiFetch } from "../../Client/NetworkActions"
import { apiEndpoints } from "../../../Routes/api"
import { safePromise } from "../../../Helpers/Functions/promise"
import {
    APIAppTemplateModel,
    TemplateName
} from "../../../Models/API/AppTemplateModel"
import { Result } from "../../../Models/Utility/Result"
import Ajv, { ErrorObject, ValidateFunction } from "ajv"
import TEMPLATE_SCHEMA from "../../../Helpers/Schema/Validation/Template.json"
import { Map } from "immutable"
import { reportError } from "../../../Helpers/Functions/sentry"

let templateValidator: ValidateFunction | undefined

function getTemplateValidator(): ValidateFunction {
    if (templateValidator !== undefined) {
        return templateValidator
    }

    templateValidator = new Ajv().compile(TEMPLATE_SCHEMA)

    return templateValidator
}

function tryCastToTemplate(
    maybeTemplate: unknown
): Result<APIAppTemplateModel> {
    const validate = getTemplateValidator()
    const isValid = validate(maybeTemplate)
    const formatError = (error: ErrorObject) =>
        `${error.message} at ${error.dataPath}`

    if (isValid) {
        return Result.ok(maybeTemplate as APIAppTemplateModel)
    }

    const message =
        validate.errors === null || validate.errors === undefined
            ? "Unknown validation error"
            : `Invalid template: ${validate.errors.map(formatError).join(", ")}`

    return Result.error(message)
}

export enum AppTemplateSortBy {
    name = "name",
    uuid = "uuid",
    created = "created"
}

export const getTemplateByAppID =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getTemplateByAppID({ appID })

        try {
            const res = await dispatch(
                apiFetch<APIAppTemplateModel>(endpoint.url, endpoint)
            )

            if (res === undefined) {
                return
            }

            dispatch({ type: TemplateAPIType.addTemplates, payload: [res] })
        } catch (error) {
            reportError(error)
        }
    }

export const getTemplateList = () => async (dispatch: MainThunkDispatch) => {
    const { data, error } = await safePromise(
        dispatch(apiFetch("FETCH_TEMPLATES", apiEndpoints.getTemplateList({})))
    )
    const templates = data === undefined ? undefined : data.templates

    if (
        error !== undefined ||
        data === undefined ||
        !Array.isArray(templates)
    ) {
        return
    }

    const { errors, templates: validTemplates } = templates
        .map(tryCastToTemplate)
        .reduce(
            (passed, result, index) => {
                if (result.isError) {
                    if (!templates[index].name && !templates[index].uuid) {
                        console.dir(templates[index])
                        console.error(
                            "The above template has an unrecognized structure."
                        )
                        return passed
                    }
                    const template = templates[index]

                    return template !== undefined
                        ? {
                              ...passed,
                              errors: passed.errors.set(
                                  template.name,
                                  result.message
                              )
                          }
                        : passed // give up if name cannot be parsed
                }

                return {
                    ...passed,
                    templates: passed.templates.concat([result.value])
                }
            },
            { errors: Map(), templates: [] } as {
                errors: Map<TemplateName, string>
                templates: APIAppTemplateModel[]
            }
        )

    dispatch({
        type: TemplateAPIType.setList,
        payload: validTemplates
    })

    dispatch({
        type: TemplateAPIType.receiveErrors,
        payload: errors
    })

    if (console.table !== undefined && errors.size > 0) {
        console.table(errors.toObject())
    }
}
